import { createSelector } from 'reselect'

import compose from 'ramda/src/compose'
import filter from 'ramda/src/filter'
import map from 'ramda/src/map'

import { parseFinancial } from '../../../lib/utils'

import { REDUCER_NAME } from './constants'
import { toSpreadsheets } from './model'

/** @type {Map<boolean, number>} */
const BOOLEAN_ORD = new Map([[false, 1], [true, -1]])

/** @type {{ [field: string]: (order: 'asc' | 'desc') => <T>(a: T, b: T) => -1 | 0 | 1 }} */
const strategy = {
  simple: order => (a, b) => {
    if (order === 'asc') return BOOLEAN_ORD.get(a < b)
    if (order === 'desc') return BOOLEAN_ORD.get(a > b)
    return 0
  },
  revenue: order => (a, b) => {
    if ([a, b].every(x => x === '-')) return 0
    if (a === '-') return 1
    if (b === '-') return -1
    return strategy.simple(order)(...[a, b].map(parseFinancial))
  },
}

/** @param {{ field: string, order: 'asc' | 'desc' }} sort */
const sortingStrategies = ({ field, order }) => {
  if (field in strategy) return strategy[field](order)
  return strategy.simple(order)
}

export const getModule = rootState => rootState.get(REDUCER_NAME)

export const getFetching = createSelector(getModule, state => state.get('fetching'))
export const getActual = createSelector(getModule, state => state.get('display_actual'))
export const getDate = createSelector(getModule, state => state.get('date').toJS())
export const getFilter = createSelector(getModule, state => state.get('filter').toJS())
export const getSort = createSelector(getModule, state => state.get('sort').toJS())
export const getGP = createSelector(getModule, state => state.get('gp_type'))
export const getValue = createSelector(getModule, state => state.get('value_type'))

export const getTable = createSelector(
  getModule,
  getFilter,
  getSort,
  (state, { lob, other }, { field, order }) => state.get('projects')
    .map(record => record.delete('gpreport'))
    .filter(project => {
      const lobCriteria = project.get('lob') === lob
      const code = project.get('code') ? project.get('code').toLowerCase() : ''
      const name = project.get('name') ? project.get('name').toLowerCase() : ''
      const client = project.get('client') ? project.get('client').toLowerCase() : ''
      const key_project_manager = project.get('key_project_manager') ? project.get('key_project_manager').toLowerCase() : ''
      const _other = other ? other.toLowerCase() : ''
      const otherCriteria = [
        code,
        name,
        key_project_manager,
        // project.get('pm').join(' '),
        client,
      ].join(' ').includes(_other)

      // handle a case when lob or other hasn't filled
      return (!lob || lobCriteria) && (!_other || otherCriteria)
    })
    .sortBy(project => project.get(field), sortingStrategies({ field, order }))
    .toJS()
)

export const getSpreadsheets = createSelector(
  getModule,
  getGP,
  getValue,
  getFilter,
  getSort,
  (state, gp, type, { lob, other }, { field, order }) =>
    compose(
      map(sheet => ({
        year: sheet.year,
        months: sheet.months,
        projects: sheet.projects.map(p => ({
          id: p.id,
          bymonths: p.bymonths.map(value => value[gp][type]),
        })),
      })),
      toSpreadsheets,
      immutable => immutable.toJS(),
      projects => projects.sortBy(
        project => project.get(field),
        sortingStrategies({ field, order })
      ),
      filter(project => {
        const lobCriteria = project.get('lob') === lob
        const code = project.get('code') ? project.get('code').toLowerCase() : ''
        const name = project.get('name') ? project.get('name').toLowerCase() : ''
        const client = project.get('client') ? project.get('client').toLowerCase() : ''
        const key_project_manager = project.get('key_project_manager') ? project.get('key_project_manager').toLowerCase() : ''
        const _other = other ? other.toLowerCase() : ''
        const otherCriteria = [
          code,
          name,
          key_project_manager,
          // project.get('pm').join(' '),
          client,
        ].join(' ').includes(_other)

        // handle a case when lob or other hasn't filled
        return (!lob || lobCriteria) && (!_other || otherCriteria)
      })
    )(state.get('projects'))
)



// WEBPACK FOOTER //
// ./src/pages/report/project-profit-sheet/selectors.js